import { useEffect, useState } from 'react'
import { NavLink, Route, Routes, Navigate, useNavigate } from 'react-router-dom'
import { isLive, api } from './api/client'
import MyAccessRoute from './routes/MyAccess'
import FindRoute from './routes/Find'
import OwnersRoute from './routes/Owners'
import LearnRoute from './routes/Learn'
import RequestsRoute from './routes/Requests'
import RegisterRoute from './routes/Register'
import AdoptionRoute from './routes/Adoption'
import LeaderboardRoute from './routes/Leaderboard'
import GroupsRoute from './routes/Groups'
import MoveFolderRoute from './routes/MoveFolder'
import StartProjectRoute from './routes/StartProject'
import { authEnabled, watchUser, signOutUser } from './auth'
import SignInGuide, { Welcome } from './components/SignInGuide'
import type { User } from 'firebase/auth'

/* The landing page is the front door. The filing wizard (routes/File.tsx) is
   hidden until filing is switched on: it offered moves the portal cannot yet make. */
const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/my-access', label: 'My access' },
  { to: '/find', label: 'Find' },
  { to: '/owners', label: 'Owners' },
  { to: '/learn', label: 'Learn' },
  { to: '/leaderboard', label: 'Leaderboard' },
]

/* Defaults to light regardless of the viewer's OS setting — a dark-mode device
   should not land the portal in dark mode uninvited. The toggle still cycles
   light -> dark -> light; it never falls back to 'system'. */
function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])
  const next = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'))
  return <button className="theme-toggle" onClick={next} aria-label={`Colour theme: ${theme}. Click to change.`}>{theme}</button>
}

/* Who's signed in, plus a way out. With Firebase Auth configured, sign-out is
   Firebase's; otherwise (the IAP-gated exec demo) it clears IAP's own session
   cookie via its documented path. */
function IdentityBadge({ user }: { user: User | null }) {
  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    if (authEnabled()) { setEmail(user?.email ?? null); return }
    let live = true
    api.whoami().then((r) => { if (live && r) setEmail(r.email) }).catch(() => {})
    return () => { live = false }
  }, [user])

  if (!email) return null

  return (
    <div className="identity">
      <span className="mono identity__email" title={email}>{email}</span>
      {authEnabled() ? (
        <button className="btn btn--ghost" onClick={() => { signOutUser() }}>Sign out</button>
      ) : (
        <a className="btn btn--ghost" href="/_gcp_iap/clear_login_cookie"
           title="Sign out of this session (Google Sign-In via Cloud Run IAP)">Sign out</a>
      )}
    </div>
  )
}

function Home() {
  const navigate = useNavigate()
  return <Welcome onContinue={() => navigate('/my-access')} cta="See my access" />
}

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(!authEnabled())
  useEffect(() => watchUser((u) => {
    setUser(u)
    setReady(true)
  }), [])

  const gated = authEnabled() && !user

  /* T4b: "Requests" shows in the nav only for people who have requests routed
     to them (owners, interim contacts). Anyone can still open /requests. */
  const [inboxOpen, setInboxOpen] = useState<number | null>(null)
  /* Owner register: shown to approvers and to people with proposals of their own. */
  const [registerWaiting, setRegisterWaiting] = useState<number | null>(null)
  /* Adoption: project-team allowlist, decided by the API; hidden for everyone else. */
  const [adoptionAllowed, setAdoptionAllowed] = useState(false)
  /* My groups: shown to people who own at least one access group (nightly snapshot). */
  const [groupsOwned, setGroupsOwned] = useState(0)
  /* Move a folder: shown when folderMoveEnabled is on for this environment. */
  const [moveEnabled, setMoveEnabled] = useState(false)
  /* Folder files: shown when folderFilesEnabled is on for this environment. */
  const [filesEnabled, setFilesEnabled] = useState(false)
  /* Start a project: shown when projectCreateEnabled is on for this environment. */
  const [projectsEnabled, setProjectsEnabled] = useState(false)
  useEffect(() => {
    if (!ready || gated) return
    let live = true
    api.getInbox().then((r) => { if (live) setInboxOpen(r.items.length ? r.open : null) }).catch(() => {})
    api.getRegisterMe().then((r) => { if (live) setRegisterWaiting(r.isApprover || r.mine > 0 ? r.waitingForMe : null) }).catch(() => {})
    api.getAdoptionMe().then((r) => { if (live) setAdoptionAllowed(r.allowed) }).catch(() => {})
    api.getMyGroups().then((r) => { if (live) setGroupsOwned(r.groups.length) }).catch(() => {})
    api.getMoveEnabled().then((r) => { if (live) setMoveEnabled(r.enabled) }).catch(() => {})
    api.getFolderFilesEnabled().then((r) => { if (live) setFilesEnabled(r.enabled) }).catch(() => {})
    api.getProjectsEnabled().then((r) => { if (live) setProjectsEnabled(r.enabled) }).catch(() => {})
    return () => { live = false }
  }, [ready, gated])

  /* Adoption: tell the API this person used the portal today. Fires when a signed-in page is
     visible, when the tab comes back into view, and on a once-a-minute check while visible, so a
     tab kept open across EAT midnight counts on the new day. Never fires while hidden. A failed
     call is retried on the next check, at most 5 times a day. The server dedupes per person/day. */
  useEffect(() => {
    if (!ready || gated || !isLive()) return
    let recordedDay = ''
    let attemptsDay = ''
    let attempts = 0
    let inFlight = false
    const eatDay = () => new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 10)
    const ping = () => {
      const day = eatDay()
      if (document.visibilityState !== 'visible' || inFlight || day === recordedDay) return
      if (attemptsDay !== day) { attemptsDay = day; attempts = 0 }
      if (attempts >= 5) return
      attempts += 1
      inFlight = true
      api.presence()
        .then((r) => { if (r?.recorded) recordedDay = day })
        .catch(() => {})
        .finally(() => { inFlight = false })
    }
    ping()
    document.addEventListener('visibilitychange', ping)
    const timer = window.setInterval(ping, 60_000)
    return () => { document.removeEventListener('visibilitychange', ping); window.clearInterval(timer) }
  }, [ready, gated])

  return (
    <>
      <header className="shell-head">
        <div className="wrap">
          <div className="shell-head__row">
            <a className="brand" href="/"><span className="dot" aria-hidden="true" />RMS</a>
            <div className="shell-head__tools">
              <IdentityBadge user={user} />
              <ThemeToggle />
            </div>
          </div>
          {!gated && (
            <nav className="nav" aria-label="Main">
              {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end}>{n.label}</NavLink>)}
              {moveEnabled && <NavLink to="/move-folder">Move a folder</NavLink>}
              {projectsEnabled && <NavLink to="/start-project">Start a project</NavLink>}
              {inboxOpen !== null && (
                <NavLink to="/requests">Requests{inboxOpen > 0 ? ` (${inboxOpen})` : ''}</NavLink>
              )}
              {registerWaiting !== null && (
                <NavLink to="/register">Register{registerWaiting > 0 ? ` (${registerWaiting})` : ''}</NavLink>
              )}
              {groupsOwned > 0 && <NavLink to="/my-groups">My groups</NavLink>}
              {adoptionAllowed && <NavLink to="/adoption">Adoption</NavLink>}
            </nav>
          )}
        </div>
      </header>

      <main className="wrap">
        {!ready ? null : gated ? <SignInGuide /> : (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/file" element={<Navigate to="/" replace />} />
          <Route path="/my-access" element={<MyAccessRoute moveEnabled={moveEnabled} filesEnabled={filesEnabled} />} />
          <Route path="/move-folder" element={<MoveFolderRoute />} />
          <Route path="/start-project" element={<StartProjectRoute me={user?.email ?? 'mock.user@dlight.com'} />} />
          <Route path="/find" element={<FindRoute />} />
          <Route path="/owners" element={<OwnersRoute />} />
          {/* My migration hidden (Tony, 29 Oct 2026: not working); old links land on My access. Page kept in routes/Migration.tsx. */}
          <Route path="/my-migration" element={<Navigate to="/my-access" replace />} />
          <Route path="/learn" element={<LearnRoute />} />
          <Route path="/requests" element={<RequestsRoute />} />
          <Route path="/register" element={<RegisterRoute />} />
          <Route path="/adoption" element={<AdoptionRoute />} />
          <Route path="/leaderboard" element={<LeaderboardRoute />} />
          <Route path="/my-groups" element={<GroupsRoute me={user?.email ?? 'mock.user@dlight.com'} />} />
        </Routes>
        )}

        {!isLive() && (
          <p className="note" style={{ marginTop: 'var(--s-3xl)', paddingTop: 'var(--s-md)', borderTop: '1px solid var(--rule)' }}>
            Running against the mock API. Set <code>VITE_API_BASE</code> to point at the
            real Cloud Run read service and <code>VITE_API_WRITE_BASE</code> at the write
            service. Nothing else changes.
          </p>
        )}
      </main>
    </>
  )
}
